import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import SocialProofPopup from './components/SocialProofPopup';
import ConsentBanner from './components/ConsentBanner';
import { upgradeToFullAnalytics, getConsent, hasFullConsent, setConsent, trackMetaPageView } from './lib/analytics';

// Route-level code splitting — each page loads only when navigated to
const Home = lazy(() => import('./pages/Home'));
const Quiz = lazy(() => import('./pages/Quiz'));
const Results = lazy(() => import('./pages/Results'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const CartPage = lazy(() => import('./pages/CartPage'));
const Store = lazy(() => import('./pages/Store'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Admin = lazy(() => import('./pages/Admin'));
const Personvern = lazy(() => import('./pages/Personvern'));
const Vilkar = lazy(() => import('./pages/Vilkar'));
const Cookies = lazy(() => import('./pages/Cookies'));
const Fornoydhetsgaranti = lazy(() => import('./pages/Fornoydhetsgaranti'));
const Helsetips = lazy(() => import('./pages/Helsetips'));
const BundlePage = lazy(() => import('./pages/BundlePage'));

// Component to handle scroll to anchor on navigation
function ScrollToAnchor() {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        // Small delay to ensure the page has rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      // Scroll to top on route change without hash
      window.scrollTo(0, 0);
    }
  }, [location]);

  return null;
}

function App() {
  const location = useLocation();
  const [consentResolved, setConsentResolved] = useState(() => getConsent() !== null);

  // Analytics initialization moved to index.js (runs before React mounts).
  // This ensures PostHog is ready when any component useEffect fires.

  // Fire Meta PageView on SPA route changes (skip initial — Pixel init handles that)
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (hasFullConsent()) {
      trackMetaPageView();
    }
  }, [location.pathname]);

  const handleConsent = useCallback((choice) => {
    setConsent(choice);
    setConsentResolved(true);
    if (choice === 'all') {
      upgradeToFullAnalytics(); // Enable session recording + Meta Pixel
    }
  }, []);

  // Disable social proof popup on quiz, results, and admin pages
  const isQuizPage = location.pathname === '/quiz';
  const isResultsPage = location.pathname === '/resultat';
  const isAdminPage = location.pathname === '/admin';

  return (
    <div className="flex flex-col min-h-screen">
      <ScrollToAnchor />
      {!isAdminPage && <Header />}
      <main className="flex-grow">
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/resultat" element={<Results />} />
            <Route path="/produkt/:slug" element={<ProductDetail />} />
            <Route path="/butikk" element={<Store />} />
            <Route path="/handlekurv" element={<CartPage />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/personvern" element={<Personvern />} />
            <Route path="/vilkar" element={<Vilkar />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/fornoydhetsgaranti" element={<Fornoydhetsgaranti />} />
            <Route path="/helsetips" element={<Helsetips />} />
            <Route path="/pakke/:slug" element={<BundlePage />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminPage && <Footer />}

      {/* Social Proof Popup - disabled on quiz and results pages */}
      <SocialProofPopup disabled={isQuizPage || isResultsPage} />

      {/* GDPR Consent Banner - only shown if no consent decision yet */}
      {!consentResolved && <ConsentBanner onConsent={handleConsent} />}
    </div>
  );
}

export default App;
